import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, MessageSquare, Users, Plus } from 'lucide-react';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard({ user }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Properties',
      value: stats?.total_properties || 0,
      icon: <Home className="w-8 h-8" />,
      color: 'bg-primary',
      link: '/admin-portal/properties'
    },
    {
      title: 'Available Properties',
      value: stats?.available_properties || 0,
      icon: <Home className="w-8 h-8" />,
      color: 'bg-accent',
      link: '/admin-portal/properties'
    },
    {
      title: 'Total Inquiries',
      value: stats?.total_inquiries || 0,
      icon: <MessageSquare className="w-8 h-8" />,
      color: 'bg-primary',
      link: '/admin-portal/inquiries'
    },
    {
      title: 'New Inquiries',
      value: stats?.new_inquiries || 0,
      icon: <MessageSquare className="w-8 h-8" />,
      color: 'bg-accent',
      link: '/admin-portal/inquiries'
    },
    {
      title: 'Testimonials',
      value: stats?.total_testimonials || 0,
      icon: <Users className="w-8 h-8" />,
      color: 'bg-primary',
      link: '/admin-portal/testimonials'
    }
  ];

  return (
    <AdminLayout user={user}>
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="mb-8">
            <h1 className="font-heading text-4xl font-medium text-primary mb-2">Dashboard</h1>
            <p className="text-[#57534E]">Welcome back, {user?.name}! Here's your overview.</p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {statCards.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <Link
                    to={stat.link}
                    data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s/g, '-')}`}
                    className="block bg-white p-6 border border-stone-200 rounded-sm hover:shadow-lg transition-shadow"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className={`w-12 h-12 ${stat.color} rounded-sm flex items-center justify-center text-white`}>
                        {stat.icon}
                      </div>
                    </div>
                    <p className="text-sm text-[#57534E] mb-1">{stat.title}</p>
                    <p className="text-3xl font-heading font-medium text-primary">{stat.value}</p>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}

          {/* Quick Actions */}
          <div className="bg-white p-8 border border-stone-200 rounded-sm">
            <h2 className="font-heading text-2xl font-medium text-primary mb-6">Quick Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link to="/admin-portal/properties/new">
                <Button
                  data-testid="add-property-button"
                  className="w-full h-12 bg-primary text-white hover:bg-primary/90 rounded-sm font-medium"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add New Property
                </Button>
              </Link>
              <Link to="/admin-portal/testimonials/new">
                <Button
                  data-testid="add-testimonial-button"
                  className="w-full h-12 bg-primary text-white hover:bg-primary/90 rounded-sm font-medium"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Testimonial
                </Button>
              </Link>
              <Link to="/admin-portal/inquiries">
                <Button
                  data-testid="view-inquiries-button"
                  className="w-full h-12 bg-primary text-white hover:bg-primary/90 rounded-sm font-medium"
                >
                  <MessageSquare className="w-5 h-5 mr-2" />
                  View Inquiries
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </AdminLayout>
  );
}